import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useUIStore } from "../stores/uiStore";
import {
  getInventoryMovements, listInventoryRuns, selectedPaintableRuns,
  type InventoryRunSummary,
} from "../api/rest";
import styles from "./Panel.module.css";

interface Props {
  siteId: string;
  warehouseId: string;
}

/** Shown rows of the movements list — the API caps the wire at 1000; the panel
 *  shows the head and says how many more there are. */
const MOVES_SHOWN = 30;

function runLabel(run: InventoryRunSummary, locale: string): string {
  const iso = run.startedAt ?? run.createdAt;
  const date = new Date(iso).toLocaleDateString(locale, { day: "2-digit", month: "2-digit" });
  return run.name ? `${date} · ${run.name}` : date;
}

/**
 * ADR 198: the /live run picker + movements reading, one section of the
 * LayerPanel. Hidden until the warehouse has a second paintable run — a
 * single-run warehouse keeps the ADR 190 latest-only behavior with no extra
 * chrome. Selecting two or more runs merges the paint (newest scan per address
 * wins) and opens the movements list between the oldest and newest selected
 * runs; clicking a moved pallet isolates its label through the ADR 196 product
 * search, so both bins light up in the scene.
 */
export function RunSelectionSection({ siteId, warehouseId }: Props) {
  const { t, i18n } = useTranslation();
  const runSelection = useUIStore((s) => s.runSelection);
  const setRunSelection = useUIStore((s) => s.setRunSelection);
  const setProductQuery = useUIStore((s) => s.setProductQuery);
  const setMovementFocus = useUIStore((s) => s.setMovementFocus);
  const movementFocus = useUIStore((s) => s.movementFocus);

  const { data: runs } = useQuery({
    queryKey: ["inventory-runs", siteId],
    queryFn: () => listInventoryRuns(siteId),
    staleTime: 30_000,
  });

  const paintable = (runs ?? []).filter(
    (r) => r.warehouseId === warehouseId && r.status !== "failed",
  );
  const effective = selectedPaintableRuns(runs ?? [], warehouseId, runSelection);
  const checked = new Set(effective.map((r) => r.id));

  // Oldest → newest of the selection: "what moved since the older count".
  const fromRun = effective.length > 1 ? effective[effective.length - 1] : null;
  const toRun = effective.length > 1 ? effective[0] : null;
  const { data: movements } = useQuery({
    queryKey: ["inventory-movements", siteId, fromRun?.id, toRun?.id],
    queryFn: () => getInventoryMovements(siteId, fromRun!.id, toRun!.id),
    enabled: fromRun != null && toRun != null,
    staleTime: 30_000,
  });

  if (paintable.length < 2) return null;

  const toggleRun = (id: string) => {
    const next = paintable.filter((r) => checked.has(r.id) !== (r.id === id)).map((r) => r.id);
    if (next.length === 0) return; // at least one run must keep painting
    setRunSelection(warehouseId, next);
  };

  return (
    <>
      <h3 className={styles.classTitle}>{t("layers.class.runs")}</h3>
      {paintable.map((run) => (
        <label key={run.id} className={styles.row}>
          <input
            type="checkbox"
            checked={checked.has(run.id)}
            onChange={() => toggleRun(run.id)}
            aria-label={runLabel(run, i18n.language)}
          />
          <span className={styles.label}>{runLabel(run, i18n.language)}</span>
        </label>
      ))}

      {movements && fromRun && toRun && (
        <div className={styles.movements}>
          <h3 className={styles.classTitle}>{t("runs.movements")}</h3>
          <p className={styles.movementCounts}>
            {t("runs.movementCounts", {
              moved: movements.movedCount,
              appeared: movements.appearedCount,
              disappeared: movements.disappearedCount,
            })}
          </p>
          {movements.moves.length === 0 ? (
            <p className={styles.empty}>{t("runs.movementsEmpty")}</p>
          ) : (
            <ul className={styles.movementList}>
              {movements.moves.slice(0, MOVES_SHOWN).map((m) => (
                <li key={`${m.product}:${m.fromAddress}`}>
                  <button
                    type="button"
                    className={styles.movementRow}
                    aria-pressed={movementFocus?.product === m.product && movementFocus.fromAddress === m.fromAddress}
                    onClick={() => {
                      setProductQuery(m.product);
                      setMovementFocus({ product: m.product, fromAddress: m.fromAddress, toAddress: m.toAddress });
                    }}
                    title={t("runs.movementIsolate")}
                  >
                    <span className={styles.movementLabel}>{m.item}</span>
                    <span className={styles.movementPath}>
                      {m.fromAddress} → {m.toAddress}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {movementFocus && <p className={styles.hint}>{t("runs.movementLegend")}</p>}
          {movements.moves.length > MOVES_SHOWN && (
            <p className={styles.empty}>
              {t("runs.movementsMore", { count: movements.moves.length - MOVES_SHOWN })}
            </p>
          )}
        </div>
      )}
    </>
  );
}
